import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';

/*
  Recorrido 3-nov (bug 2): la entrada de la empresa es `/dashboard` (login, `/` y «Volver
  al inicio»). Una empresa sin el módulo del Inicio caía en «Este módulo no está incluido»
  y «Volver al inicio» la devolvía ahí: un bucle sin salida. La regla, en un solo lugar:
  se entra a la PRIMERA pantalla que la empresa y el rol abren, en el orden del menú; sin
  ninguna, a Mi perfil (libre: es de la persona). Vale para cualquier empresa y plan; nada
  depende de una empresa ni de un plan concreto.

  Cada entrada es la pantalla de llegada de UN módulo (`menu:<modulo>`). El guardián de
  `primera-pantalla.test.tsx` verifica que cada ruta pertenezca de verdad a su módulo en
  `route-modules.ts`: si una pantalla se muda de módulo, el test lo avisa.
*/
export const ENTRADAS_POR_MODULO: ReadonlyArray<{ modulo: string; ruta: string }> = [
  { modulo: 'dashboard', ruta: '/dashboard' },
  { modulo: 'pricing', ruta: '/compite/monitoreo' },
  { modulo: 'data-import', ruta: '/compite/decisiones' },
  { modulo: 'nueva-cotizacion', ruta: '/quotes/new' },
  { modulo: 'quotes', ruta: '/quotes' },
  { modulo: 'approvals', ruta: '/approvals' },
  { modulo: 'pricer-datec', ruta: '/pricer-datec' },
  { modulo: 'pricer-solar', ruta: '/pricer-solar' },
  { modulo: 'customers', ruta: '/customers' },
  { modulo: 'ai-intelligence', ruta: '/ai-intelligence' },
  { modulo: 'analytics', ruta: '/analytics' },
  { modulo: 'import-tracking', ruta: '/comercia' },
  { modulo: 'trade-cotizador', ruta: '/trade/cotizador' },
  { modulo: 'trade-ordenes', ruta: '/trade/cotizaciones' },
  { modulo: 'trade-tarifario', ruta: '/trade/tarifario' },
  { modulo: 'trade-aduana', ruta: '/trade/aduana' },
  { modulo: 'master-data', ruta: '/master-data' },
  { modulo: 'integrations', ruta: '/integrations' },
  { modulo: 'admin', ruta: '/mi-empresa' },
];

/** Sin ninguna pantalla de módulo: Mi perfil (ruta libre). */
export const PANTALLA_SIN_MODULOS = '/profile';

/** La regla pura: `abre(modulo)` dice si la empresa lo tiene y el rol no lo tiene bloqueado. */
export function primeraPantalla(abre: (modulo: string) => boolean): string {
  return ENTRADAS_POR_MODULO.find((e) => abre(e.modulo))?.ruta ?? PANTALLA_SIN_MODULOS;
}

/**
 * La primera pantalla de la sesión, o `null` si todavía no se sabe qué módulos tiene
 * (sesión sin la lista): entonces no se mueve a nadie, como `ProtectedRoute`.
 */
export function usePrimeraPantalla(): string | null {
  const { menuItems, isModuleLocked } = useAuth();
  if (menuItems.length === 0) return null;
  return primeraPantalla((m) => menuItems.includes(`menu:${m}`) && !isModuleLocked(m));
}

/**
 * Envuelve la entrada (`/dashboard`): si la empresa o el rol no abren el Inicio, lleva a
 * la primera pantalla que sí abren. Con sesión todavía cargando o sin lista, no hace nada
 * (lo resuelve `ProtectedRoute`).
 */
export function EntradaALaPrimeraPantalla({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const destino = usePrimeraPantalla();
  if (!isLoading && isAuthenticated && destino && destino !== '/dashboard') {
    return <Navigate to={destino} replace />;
  }
  return <>{children}</>;
}
